
import React, { useState, useEffect } from 'react';
import { db } from '../../../../firebase';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { MemberModel, StudentOMRModel } from '../../../../models';
import { Calculator, Trophy, Search } from 'lucide-react';
import OMRHistoryCard from './OMRHistoryCard';
import OMRDetailView from './OMRDetailView';
import Header from '../studentFrame/header';

interface StudentOMRViewProps {
    member: MemberModel;
    onViewDetail: (result: StudentOMRModel) => void;
}

const OMRHistory: React.FC<StudentOMRViewProps> = ({ member, onViewDetail }) => {
    const [results, setResults] = useState<StudentOMRModel[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSubject, setSelectedSubject] = useState<string>('전체');
    const [selectedResult, setSelectedResult] = useState<StudentOMRModel | null>(null);

    useEffect(() => {
        const q = query(
            collection(db, 'studentOMR'),
            where('memberId', '==', member.id)
        );

        const unsubscribe = onSnapshot(q, (snap) => {
            const list: StudentOMRModel[] = [];
            snap.forEach(doc => list.push({ id: doc.id, ...doc.data() } as StudentOMRModel));
            // Client-side sort instead of Firestore orderBy to avoid index requirement
            list.sort((a, b) => (b.completedAt || 0) - (a.completedAt || 0));
            setResults(list);
            setIsLoading(false);
        }, (err) => {
            console.error("Error fetching OMR results:", err);
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [member.id]);

    const subjects = ['전체', ...Array.from(new Set(results.map(r => r.subject).filter(Boolean)))];

    const filteredResults = results.filter(res => {
        const matchesSearch = res.bookTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
            res.setTitle.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesSubject = selectedSubject === '전체' || res.subject === selectedSubject;
        return matchesSearch && matchesSubject;
    });

    if (selectedResult) {
        return (
            <OMRDetailView
                result={selectedResult}
                onBack={() => setSelectedResult(null)}
            />
        );
    }

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            {/* Header Section */}
            {/* Header Section */}
            <Header
                icon={Calculator}
                title="OMR 채점 기록"
                subtitle="내가 제출하고 채점한 모든 OMR 기록입니다."
                rightElement={
                    <div className="bg-slate-50 px-5 py-2.5 rounded-xl border border-slate-100">
                        <span className="block text-[10px] uppercase font-bold text-slate-400 mb-0.5">총 제출 횟수</span>
                        <span className="text-lg font-black text-slate-800">{results.length}회</span>
                    </div>
                }
            />

            {/* Filter & Search Section */}
            <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                    <input
                        type="text"
                        placeholder="교재명 또는 세트 제목으로 검색..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full bg-white border border-slate-100 rounded-xl pl-11 pr-4 py-3 text-sm font-medium shadow-sm focus:ring-2 focus:ring-indigo-100 outline-none transition-all placeholder:text-slate-300"
                    />
                </div>
                <div className="flex items-center gap-2 overflow-x-auto no-scrollbar py-1">
                    {subjects.map(sub => (
                        <button
                            key={sub}
                            onClick={() => setSelectedSubject(sub)}
                            className={`px-4 py-2.5 rounded-lg text-xs font-bold whitespace-nowrap transition-all ${selectedSubject === sub
                                ? 'bg-indigo-600 text-white shadow-sm'
                                : 'bg-white text-slate-400 border border-slate-100 hover:border-indigo-200'
                                }`}
                        >
                            {sub}
                        </button>
                    ))}
                </div>
            </div>

            {/* Results List */}
            {isLoading ? (
                <div className="flex flex-col items-center justify-center py-20 gap-4">
                    <div className="w-12 h-12 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin"></div>
                    <p className="text-slate-400 font-bold">기록을 불러오는 중입니다...</p>
                </div>
            ) : filteredResults.length > 0 ? (
                <div className="space-y-4">
                    {filteredResults.map((res) => (
                        <OMRHistoryCard
                            key={res.id}
                            result={res}
                            onView={() => setSelectedResult(res)}
                        />
                    ))}
                </div>
            ) : (
                <div className="py-20 text-center bg-white rounded-[3rem] border border-slate-100 shadow-sm border-dashed">
                    <Trophy size={48} className="mx-auto text-slate-100 mb-4" />
                    <h3 className="text-lg font-black text-slate-800">아직 제출된 OMR이 없습니다</h3>
                    <p className="text-sm font-bold text-slate-400 mt-1">학습 일정 상세 보기에서 OMR 문제를 풀고 채점해 보세요!</p>
                </div>
            )}
        </div>
    );
};

export default OMRHistory;
